/**
 * Lightweight typed wrapper around Google Analytics 4's `gtag` API.
 *
 * GA is loaded inline in `index.html` via the gtag.js snippet, so by the
 * time React mounts `window.gtag` is available — but adblockers, dev
 * environments, and tests will leave it undefined. Every call here checks
 * for that and silently no-ops, so callers don't need defensive code.
 *
 * Two responsibilities:
 *  1. SPA page-view tracking. The default GA snippet only fires once on
 *     initial load; it doesn't see React Router navigations. The
 *     `useAnalyticsPageView` hook drives manual `page_view` events on every
 *     route change.
 *  2. Custom events for high-signal user actions (signup, lead creation,
 *     field-app grab, install start, etc.) so we can build funnels in the
 *     GA UI beyond raw pageviews.
 *
 * Custom dimensions used on events here (`app_section`, `tenant_subdomain`,
 * `user_role`) must be registered once in the GA4 admin (Admin → Custom
 * Definitions → Custom Dimensions) before they show up as report
 * dimensions; until then the values still arrive but only show up under
 * "Event parameters" debug views.
 */

declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'set' | 'js',
      targetIdOrEventName: string | Date,
      params?: Record<string, unknown>,
    ) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Coarse-grained section of the app the user is currently in. Drives the
 * `app_section` custom dimension so we can answer "are users on the field
 * PWA or the dashboard?" without inspecting full URLs.
 */
export type AppSection =
  | 'dashboard'
  | 'field_app'
  | 'customer_portal'
  | 'contractor'
  | 'superadmin'
  | 'signup'
  | 'auth'
  | 'embed'
  | 'marketing';

/**
 * Map a pathname to an app section. Order matters — most specific prefixes
 * first. The fallback is `marketing` since the public site lives at the
 * site root (`/`, `/pricing`, `/blog`, etc.).
 */
export function getAppSection(pathname: string): AppSection {
  if (pathname.startsWith('/field')) return 'field_app';
  if (pathname.startsWith('/portal')) return 'customer_portal';
  if (pathname.startsWith('/contractor')) return 'contractor';
  if (pathname.startsWith('/superadmin')) return 'superadmin';
  if (pathname.startsWith('/embed')) return 'embed';
  if (pathname.startsWith('/signup')) return 'signup';
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (
    pathname.startsWith('/auth') ||
    pathname === '/login' ||
    pathname === '/register' ||
    pathname.startsWith('/register-isp') ||
    pathname.startsWith('/register/') ||
    pathname === '/forgot-password' ||
    pathname === '/reset-password'
  ) {
    return 'auth';
  }
  return 'marketing';
}

/**
 * Names for custom events. Listed here as a union so callers can't fat-
 * finger an event name and silently land in the wrong report.
 */
export type AnalyticsEventName =
  // Signup funnel
  | 'signup_lead_created'
  | 'signup_step_completed'
  | 'signup_address_checked'
  | 'signup_submit_failed'
  // Install deposit (down payment) step
  | 'signup_deposit_started'
  | 'signup_deposit_paid'
  | 'signup_deposit_failed'
  // Lead admin
  | 'lead_created_admin'
  // Field app actions
  | 'field_job_claimed'
  | 'field_job_grabbed_and_started'
  | 'field_job_started'
  | 'field_job_completed'
  // Install lifecycle
  | 'install_completed';

interface AnalyticsContext {
  userId?: string;
  userRole?: string;
  tenantSubdomain?: string;
  appSection?: AppSection;
}

let currentContext: AnalyticsContext = {};

/**
 * Update the ambient context attached to every subsequent event. Set from
 * `useAnalyticsPageView` whenever the user / tenant / section changes —
 * callers that fire custom events don't have to thread this through.
 */
export function setAnalyticsContext(context: AnalyticsContext): void {
  currentContext = { ...currentContext, ...context };

  // Push the user-id + role as GA user properties so they're available for
  // user-scoped reports (audiences, retention) — not just event parameters.
  // user_role is sent as a custom user property; user_id is GA's first-class
  // identifier and persists across sessions on the same device.
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  const setPayload: Record<string, unknown> = {};
  if (context.userId !== undefined) {
    setPayload.user_id = context.userId;
  }
  if (context.userRole !== undefined) {
    setPayload.user_properties = { user_role: context.userRole };
  }
  if (Object.keys(setPayload).length > 0) {
    window.gtag('set', 'user', setPayload);
  }
}

/**
 * Fire a `page_view` event for SPA route changes. The GA snippet sets
 * `send_page_view: false` so this is the only place pageviews originate.
 */
export function trackPageView(pathname: string, title?: string): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_title: title ?? document.title,
    page_location: window.location.href,
    app_section: currentContext.appSection,
    tenant_subdomain: currentContext.tenantSubdomain,
    user_role: currentContext.userRole,
  });
}

/**
 * Fire a custom event with the given parameters. Adds the ambient context
 * (section / tenant / role) automatically so every event is sliceable in
 * the GA UI without each caller threading those values through.
 */
export function trackEvent(
  name: AnalyticsEventName,
  params: Record<string, unknown> = {},
): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', name, {
    ...params,
    app_section: currentContext.appSection,
    tenant_subdomain: currentContext.tenantSubdomain,
    user_role: currentContext.userRole,
  });
}

// Test-only escape hatch — lets vitest reset between cases without exporting
// the mutable `currentContext` directly. Not part of the runtime API.
export function __resetAnalyticsContextForTests(): void {
  currentContext = {};
}

// IDs already handed to `gtag('config', ...)` this page load. A tenant's GA4
// property only needs (and should only get) one `config` call per id — a
// second call would reset that property's session. Module-level so it
// survives across the signup hook's re-renders and effect re-runs.
let configuredMeasurementIds = new Set<string>();

/**
 * Registers a second GA4 property (the tenant's own measurement ID) with the
 * `gtag` snippet already loaded for the platform property. Once a config id
 * has been passed to `gtag('config', ...)`, gtag broadcasts every subsequent
 * `event` and `page_view` call to *all* configured ids — so calling this
 * once, as soon as the tenant config resolves, is enough to make every
 * later `trackEvent` / `trackPageView` call land in both the platform's GA
 * property and the tenant's.
 *
 * No-ops when `gtag` isn't loaded (adblock, tests, SSR), when `id` is empty,
 * or when this id has already been configured this page load.
 */
export function configureTenantMeasurementId(id: string | null | undefined): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  if (!id) {
    return;
  }
  if (configuredMeasurementIds.has(id)) {
    return;
  }
  configuredMeasurementIds.add(id);
  window.gtag('config', id, { send_page_view: false });
}

// Test-only escape hatch — mirrors __resetAnalyticsContextForTests above.
export function __resetConfiguredMeasurementIdsForTests(): void {
  configuredMeasurementIds = new Set();
}
